import {
  useShowInstructions,
  clamp,
  randomInt,
} from '@backstage-components/base';
import {type FC, useLayoutEffect, useRef} from 'react';
import {useSubscription} from 'observable-hooks';
import {
  ComponentDefinition,
  DEFAULT_CONFETTI_COLORS,
} from './ConfettiDefinition';
import {cx, css} from '@emotion/css';
import {
  ConfettiComponentDefinition,
  JSConfettiType,
  IAddConfettiConfig,
} from './Confetti.types';
import JSConfetti from 'js-confetti';
import * as Styled from './Confetti.styled';

const emojiBlastCountCap = 60;

export const ConfettiComponent: FC<ConfettiComponentDefinition> = (
  definition
) => {
  const {props} = definition;
  const {
    confettiRadius,
    confettiNumber,
    confettiColors,
    emojiConfetti,
    partyBlastCount,
  } = props;
  const {useEmojiConfetti, emojis, emojiSize, alternate, emojiConfettiNumber} =
    emojiConfetti || {};

  const emojiProps = {
    emojis: useEmojiConfetti && emojis ? emojis : undefined,
    emojiSize: useEmojiConfetti && emojiSize ? emojiSize : undefined,
  };

  const confettiProps = {
    confettiColors: confettiColors ?? DEFAULT_CONFETTI_COLORS,
    confettiRadius,
    confettiNumber,
  };

  const jsConfetti = useRef<JSConfettiType | null>(null);
  const emojiBlastMode = useRef<boolean>(useEmojiConfetti || false);
  const confettiCanvasElement = useRef<HTMLCanvasElement | null>(null);

  const styleClassName = css`
    ${definition.style}
    ${definition.props.styleAttr}
  `;

  const updateBlastMode = (): void => {
    if (useEmojiConfetti && alternate) {
      emojiBlastMode.current = !emojiBlastMode.current;
    }
  };

  const getAddConfettiProps = (): IAddConfettiConfig => {
    return {
      ...confettiProps,
      ...(emojiBlastMode.current && emojiProps),
      // Emoji blasts need to have a max for performance reasons
      ...(emojiBlastMode.current && {
        confettiNumber: clamp(
          emojiConfettiNumber || emojiBlastCountCap,
          1,
          emojiBlastCountCap
        ),
      }),
    };
  };

  const {observable, broadcast} = useShowInstructions(
    ComponentDefinition.instructions,
    definition
  );
  useSubscription(observable, (inst) => {
    if (inst.type === 'Confetti:fire-confetti') {
      if (jsConfetti.current) {
        const confettiProps = getAddConfettiProps();
        // Fires the confetti
        jsConfetti.current.addConfetti(confettiProps).then(() =>
          broadcast({
            type: 'Confetti:on-confetti-complete',
            meta: {},
          })
        );

        updateBlastMode();
        broadcast({
          type: 'Confetti:on-confetti-fire',
          meta: {},
        });
      }
    }

    if (inst.type === 'Confetti:confetti-party') {
      if (jsConfetti.current) {
        // The number of confetti blasts to send during a "confetti party"
        // We start with 10 as a default, but this can be overridden by settings, or by the flow controller
        let count = partyBlastCount || 10;
        if (inst.meta.blastCount) {
          count = parseInt(inst.meta.blastCount, 10);
        }
        const confettiBlasts: Promise<void>[] = [];
        const jsConfettiRef = jsConfetti.current;
        // Keep track of the time for each blast
        let currentTime = 0;
        for (let i = 0; i < count; i++) {
          currentTime = currentTime + randomInt(250, 850);
          setTimeout(() => {
            // Fires the confetti
            const confettiProps = getAddConfettiProps();
            // Ensure the canvas element still exists
            if (!jsConfettiRef) return;
            const blast = jsConfettiRef.addConfetti(confettiProps);
            confettiBlasts.push(blast);
            updateBlastMode();

            // All confetti blasts have been added
            if (i === count - 1) {
              // All confetti blasts from the party have completed animating
              Promise.all(confettiBlasts).then(() => {
                broadcast({
                  type: 'Confetti:on-confetti-complete',
                  meta: {},
                });
              });
            }
          }, currentTime);
        }

        // Confetti party has fired
        broadcast({
          type: 'Confetti:on-confetti-fire',
          meta: {},
        });
      }
    }
  });

  useLayoutEffect(() => {
    if (confettiCanvasElement.current) {
      const canvas = confettiCanvasElement.current as HTMLCanvasElement;
      jsConfetti.current = new JSConfetti({canvas});
    }

    return () => {
      if (jsConfetti.current) jsConfetti.current.clearCanvas();
    };
  }, []);

  return (
    <Styled.ConfettiCanvas
      id="confetti-canvas"
      role="canvas"
      className={cx(styleClassName, definition.mid)}
      ref={confettiCanvasElement}
    />
  );
};
